import { useMemo, useRef } from 'react';
import { useListBlockedNumbersQueries } from '../queries/use-list-blocked-numbers';
import { BlockedNumber } from '../types';

export type BlockedNumbersByTenant = Record<string, BlockedNumber[]>;

const EMPTY_LIST: BlockedNumber[] = [];

const isShallowEqual = (a: BlockedNumbersByTenant, b: BlockedNumbersByTenant) => {
  const aKeys = Object.keys(a);
  return aKeys.length === Object.keys(b).length && aKeys.every((key) => a[key] === b[key]);
};

/**
 * Fetches the blocked numbers of every given tenant and returns them keyed by tenant id.
 *
 * This is the only place that knows how multiple tenants are fetched. Today that is one request per tenant, each
 * stored in its own per-tenant cache entry. When the batch endpoint is available, only this hook should change: fetch
 * once and write each tenant's list into the per-tenant `ListBlockedNumbers` entries, so consumers and mutations are
 * unaffected.
 */
export const useBlockedNumbersByTenant = (tenantIds: string[]) => {
  // A stable string key so callers can pass a new array on every render.
  const idsKey = Array.from(new Set(tenantIds.filter(Boolean)))
    .sort()
    .join(',');
  const ids = useMemo(() => (idsKey ? idsKey.split(',') : []), [idsKey]);

  const queries = useListBlockedNumbersQueries(
    ids.map((tenantId) => ({
      request: { tenantId },
      options: {
        meta: {
          error: {
            message: 'useBlockedNumbersByTenant | Failed to fetch blocked numbers',
            data: { tenantId },
          },
        },
      },
    }))
  );

  const next = Object.fromEntries(ids.map((id, index) => [id, queries[index]?.data?.blockedNumbers ?? EMPTY_LIST]));
  // Keep the same object until a tenant's list actually changes, so memoized consumers don't recompute every render.
  const stableRef = useRef<BlockedNumbersByTenant>(next);
  if (!isShallowEqual(stableRef.current, next)) {
    stableRef.current = next;
  }

  return {
    blockedNumbersByTenant: stableRef.current,
    isLoading: queries.some((query) => query.isLoading),
    isError: queries.some((query) => query.isError),
  };
};
